import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What does DRIP mean in dividend investing?',
    a: 'DRIP stands for Dividend Reinvestment Plan. Instead of receiving your dividend as cash, your broker automatically uses it to buy more shares (or fractional shares) of the same company. This compounds your share count over time, which compounds the next dividend payment, and so on — the snowball that long-term dividend investors talk about.',
  },
  {
    q: 'Is DRIP a good idea?',
    a: 'For most long-term holders of quality dividend payers, yes. DRIP enforces dollar-cost averaging, removes the friction of manually reinvesting, and lets you build a position with no commissions on the reinvestment. The downside: you give up control over WHEN you reinvest. If the stock is overpriced, you keep buying anyway. For retirees who need cash, DRIP off; for accumulators in their 20s-50s, DRIP on.',
  },
  {
    q: 'Are reinvested dividends taxed?',
    a: 'Yes, reinvested dividends are taxed in the same year they are paid, even though you never see the cash. Your broker reports them on your 1099-DIV. The tax treatment depends on whether the dividend is "qualified" (long-term capital gains rates) or "ordinary" (your regular income tax rate). Holding in a Roth or traditional IRA defers or eliminates that tax.',
  },
  {
    q: 'How do I turn DRIP on or off?',
    a: 'Inside your broker (Fidelity, Schwab, Robinhood, etc.) there is a per-position toggle for dividend reinvestment. Flip it to "Reinvest" or "Cash". DiviDrip tracks both — log each buy as Source = Buy for cash purchases, Source = DRIP for reinvestment purchases. If you forgot to mark a lot as DRIP at the time, click Show Shares on the ticker row and tap the hollow water-drop icon to flip it.',
  },
  {
    q: 'Does DiviDrip auto-detect DRIP buys?',
    a: 'When a dividend reinvestment occurs, DiviDrip will surface a payout reminder on the affected portfolio stock with an "Add DRIP Buy" button. You enter the number of shares your broker bought and the per-share price, and the new shares fold into your position.',
  },
];

export default function LearnWhatIsDrip() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="What is DRIP? Dividend Reinvestment Explained (2026) | DiviDrip"
      description="Descriptive Guide to DRIP (Dividend Reinvestment Plans) — how reinvestment works, when it makes sense, tax rules, and how DiviDrip tracks every reinvested share."
      canonical="https://twylightcrow.com/learn/what-is-drip"
      guidePath="/learn/what-is-drip"
      sectionLabel="DRIP & Reinvestment Basics"
      sectionAnchor="basics"
      kicker="Learn · DRIP basics"
      h1="What is DRIP? Dividend Reinvestment Explained"
      datePublished="2026-05-22"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/drip-vs-cash-dividends', label: 'DRIP vs cash dividends — which to choose?' },
        { to: '/learn/why-drip-roth', label: 'Why I DRIP in my Roth but not in my taxable' },
        { to: '/learn/four-phases', label: 'The 4 phases of a dividend investor\u2019s life' },
        { to: '/learn/yield-on-cost', label: 'Yield on cost explained' },
        { to: '/learn/dividend-kings-vs-aristocrats-vs-achievers', label: 'Dividend Kings vs Aristocrats vs Achievers — which tier matters?' },
        { to: '/learn/jepi-vs-jepq', label: 'JEPI vs JEPQ — covered-call yield face-off' },
        { to: '/learn/schd-vs-voo', label: 'SCHD vs VOO — dividend ETF vs total-market ETF' },
        { to: '/how-to', label: 'DiviDrip How-To — log your first DRIP buy in 60 seconds' },
        { to: '/glossary', label: 'Glossary — every dividend term defined' },
      ]}
    >
      <p>
        <strong>DRIP</strong> stands for <strong>Dividend Reinvestment Plan</strong>.
        When a stock or ETF you own pays a dividend, instead of the cash hitting your account
        you tell your broker to automatically use that cash to buy more shares of the same
        security. Over time you accumulate shares without ever transferring fresh money in —
        the dividends do it for you.
      </p>

      <h2>The compounding math (why DRIP matters)</h2>
      <p>
        Picture 100 shares of a stock paying a $0.50 quarterly dividend at $40 per share.
        Your first quarter: <code>100 × $0.50 = $50</code>, which buys you 1.25 more shares.
        Next quarter you own 101.25 shares, so the dividend is <code>$50.625</code>. The
        difference is trivial in quarter 2, but after <strong>20 years</strong> of consistent
        reinvestment and modest dividend growth, your share count can more than double from
        DRIP alone — before counting any price appreciation.
      </p>

      <h2>When DRIP makes sense — and when it doesn&apos;t</h2>
      <ul>
        <li><strong>Accumulators (20s — late 50s)</strong>: DRIP almost always. You don&apos;t need the cash, the reinvestment is free, and you eliminate the &ldquo;I&apos;ll reinvest tomorrow&rdquo; behavioural drag.</li>
        <li><strong>Retirees / income takers</strong>: DRIP off. You want the cash to live on, not more shares.</li>
        <li><strong>Holding overvalued stocks</strong>: DRIP off, then manually deploy the cash into something else. DRIP keeps buying regardless of price.</li>
        <li><strong>Taxable accounts</strong>: DRIP still works, but you owe tax in the year of payment. Holding in a Roth or 401(k) skips that.</li>
      </ul>

      <h2>How DiviDrip tracks DRIP buys</h2>
      <p>
        Every share lot on DiviDrip has a <strong>Source</strong> field: <em>Buy</em> for cash
        purchases or <em>DRIP</em> for reinvestment. There are three ways to mark a buy as DRIP:
      </p>
      <ol>
        <li>Inside the Stock Modal&apos;s My Portfolio tab, set Source = DRIP at the time of the buy.</li>
        <li>When a payout reminder card appears, click <em>Add DRIP Buy</em>.</li>
        <li>On the Portfolio page, click <em>Show Shares</em> on a ticker row — every lot row has a hollow water-drop icon you can tap to flip the lot to DRIP after the fact.</li>
      </ol>
      <p>
        That distinction matters for cost basis. A &ldquo;regular&rdquo; buy increases your
        outside-money contributions (real out-of-pocket); a DRIP buy doesn&apos;t — it&apos;s
        portfolio-generated. DiviDrip&apos;s Capital Tracker keeps the two columns separate so
        your <em>true</em> return is the honest number, not inflated by reinvestments.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Try it</h2>
      <p>
        Open <Link to="/dashboard">DiviDrip</Link> for free (no card, no email upsell), add a few
        dividend tickers, and watch the DRIP simulator project 5, 10, and 20 years out at
        each stock&apos;s actual historical CAGR. You can compare scenarios side by side or
        run the portfolio-wide DRIP calculator under <Link to="/dashboard?view=portfolio">My Portfolio &rarr; Tool Box</Link>.
      </p>
    </LearnPage>
  );
}
